import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  isOnlySelfStateClear,
  touchesPenPathGeometry,
  touchesPlacementGeometry,
} from './helpers/element-geometry-keys'
import { isTextUnderLockedAncestor } from '@/lib/groups/text-under-group'
import type { EditGuardBypassOpts } from '@/lib/groups/types/edit-guard-bypass-opts'

function isBypassed(opts?: EditGuardBypassOpts): boolean {
  return opts?.bypassEditGuards === true || opts?.bypassLockGuard === true
}

function isShapeAncestorLocked(shape: ShapeData, shapesById: Map<string, ShapeData>): boolean {
  let id: string | undefined = shape.parentGroupId

  while (id) {
    const ancestor = shapesById.get(id)

    if (!ancestor) break
    if (ancestor.locked === true) return true
    id = ancestor.parentGroupId
  }

  return false
}

export function isShapeUnderFrozenGroupAncestor(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>
): boolean {
  return isShapeAncestorFrozen(shape, shapesById)
}

export function isTextUnderFrozenGroupAncestor(
  text: TextBoxData,
  shapesById: Map<string, ShapeData>
): boolean {
  return isTextAncestorFrozen(text, shapesById)
}

function isShapeAncestorFrozen(shape: ShapeData, shapesById: Map<string, ShapeData>): boolean {
  let id: string | undefined = shape.parentGroupId

  while (id) {
    const ancestor = shapesById.get(id)

    if (!ancestor) break
    if (ancestor.frozen === true) return true
    id = ancestor.parentGroupId
  }

  return false
}

function isTextAncestorFrozen(text: TextBoxData, shapesById: Map<string, ShapeData>): boolean {
  let gid: string | undefined = text.parentGroupId

  while (gid) {
    const ancestor = shapesById.get(gid)

    if (!ancestor) break
    if (ancestor.frozen === true) return true
    gid = ancestor.parentGroupId
  }

  return false
}

/**
 * True when user-driven edits to this shape should be fully rejected (except `{ locked: false }`
 * handled in `ShapeManager.updateShape`).
 *
 * A locked **group** still participates in scene-graph transforms; use
 * {@link shouldSkipPropagatedGroupTransformOnElement} to omit locked/frozen **non-group**
 * descendants from parent group geometry propagation.
 */
export function isShapeLockedForUserEdit(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>
): boolean {
  if (shape.locked === true) return true

  return isShapeAncestorLocked(shape, shapesById)
}

/**
 * True when placement geometry (x, y, width, height, rotation) must not change for this shape.
 * Does not include lock — use {@link isShapeLockedForUserEdit} for full edit blocks.
 */
export function isShapeFrozenForGeometry(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>
): boolean {
  if (shape.frozen === true) return true

  return isShapeAncestorFrozen(shape, shapesById)
}

/** @deprecated Use {@link isShapeLockedForUserEdit}. */
export const isShapeFrozenForUserEdit = isShapeLockedForUserEdit

/** Locked or frozen leaves stay fixed when an ancestor group's canvas geometry changes. */
export function shouldSkipPropagatedGroupTransformOnElement(shape: ShapeData): boolean {
  if (isGroupShape(shape)) return false

  return shape.locked === true || shape.frozen === true
}

/** @deprecated Use {@link shouldSkipPropagatedGroupTransformOnElement}. */
export const shouldSkipPropagatedGroupTransformOnShape = shouldSkipPropagatedGroupTransformOnElement

export function isTextLockedForUserEdit(
  text: TextBoxData,
  shapesById: Map<string, ShapeData>
): boolean {
  return text.locked === true || isTextUnderLockedAncestor(text, shapesById)
}

export function isTextFrozenForGeometry(
  text: TextBoxData,
  shapesById: Map<string, ShapeData>
): boolean {
  return text.frozen === true || isTextAncestorFrozen(text, shapesById)
}

/** Omit from unified move/resize/rotate sessions (locked or frozen, incl. ancestors). */
export function isShapeExcludedFromUnifiedGeometryTransform(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>
): boolean {
  return isShapeLockedForUserEdit(shape, shapesById) || isShapeFrozenForGeometry(shape, shapesById)
}

export function isTextExcludedFromUnifiedGeometryTransform(
  text: TextBoxData,
  shapesById: Map<string, ShapeData>
): boolean {
  return isTextLockedForUserEdit(text, shapesById) || isTextFrozenForGeometry(text, shapesById)
}

/** Block starting a canvas move/resize/rotate gesture on this shape. */
export function isShapeGeometryGestureBlocked(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>
): boolean {
  return isShapeExcludedFromUnifiedGeometryTransform(shape, shapesById)
}

export function shouldBlockShapeUpdate(
  shape: ShapeData,
  updates: Partial<ShapeData>,
  shapesById: Map<string, ShapeData>,
  opts?: EditGuardBypassOpts
): boolean {
  if (isBypassed(opts)) {
    return false
  }

  if (isOnlySelfStateClear(updates, 'locked', false)) {
    return false
  }

  if (isOnlySelfStateClear(updates, 'frozen', false)) {
    return false
  }

  if (isShapeLockedForUserEdit(shape, shapesById)) {
    return true
  }

  if (isShapeFrozenForGeometry(shape, shapesById) && touchesPlacementGeometry(updates)) {
    return true
  }

  if (isShapeFrozenForGeometry(shape, shapesById) && touchesPenPathGeometry(updates)) {
    return true
  }

  return false
}

/** True when the user may enter pen path node-edit for this shape. */
export function canEnterPenPathNodeEdit(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>
): boolean {
  if (shape.type !== 'path' || !shape.path?.trim()) return false
  if (isShapeLockedForUserEdit(shape, shapesById)) return false
  if (isShapeFrozenForGeometry(shape, shapesById)) return false

  return true
}
